import React, {useState} from 'react';
import {Box, Breadcrumbs, Button, CircularProgress, FormControl, Grid, InputLabel, MenuItem, Select, TextField, Typography} from '@mui/material';
import api from '../../api';
import QuotaBlockedNotice from '../../components/subscription/QuotaBlockedNotice';
import SalesChannelSelect from '../../components/checkout/SalesChannelSelect';
import {Link, useNavigate} from 'react-router-dom';
import DidNotLoad from "../../components/DidNotLoad";
import PhoneInput from "../../components/PhoneInput";
import DocumentInput from "../../components/DocumentInput";
import ZipCodeInput from "../../components/ZipCodeInput";
import StateSelectInput from "../../components/StateSelectInput";
import { CUSTOMER_CATEGORY_CLIENTE, CUSTOMER_CATEGORY_OPTIONS } from '../../helpers/customers';

function RegisterCustomerPage() {
    const [name, setName] = useState('');
    const [category, setCategory] = useState(CUSTOMER_CATEGORY_CLIENTE);
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [documentNumber, setDocumentNumber] = useState('');
    const [address, setAddress] = useState('');
    const [addressNumber, setAddressNumber] = useState('');
    const [addressComplement, setAddressComplement] = useState('');
    const [zipCode, setZipCode] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [neighborhood, setNeighborhood] = useState('');
    const [originSalesChannelUuid, setOriginSalesChannelUuid] = useState('');
    const [originSalesChannelName, setOriginSalesChannelName] = useState('');

    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        document_number: null,
        name: null,
        category: null,
        origin_sales_channel_uuid: null,
        email: null,
        phone: null,
        address: null,
        address_number: null,
        address_complement: null,
        zip_code: null,
        city: null,
        state: null,
        neighborhood: null,
        has_errors: false
    });
    const [didNotLoad, setDidNotLoad] = useState(false);

    const handleOriginChannelChange = (channel) => {
        setOriginSalesChannelUuid(channel?.uuid || channel?.id || '');
        setOriginSalesChannelName(channel?.name || '');
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        let hasError = false
        const nextErrors = {};
        if (name === '') {
            hasError = true;
            nextErrors.name = 'O nome do cliente é obrigatório.';
        }
        if (!originSalesChannelUuid) {
            hasError = true;
            nextErrors.origin_sales_channel_uuid = 'Informe a origem do cadastro.';
        }

        if (hasError) {
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                ...nextErrors,
                has_errors: true,
            }));
            return;
        }

        if (isSubmitting) return; // Evita duplo clique
        setIsSubmitting(true); // Ativa o estado de loading
        let hasResponse = false;

        const newCustomer = {
            document: documentNumber.trim() === '' ? null : documentNumber,
            name: name,
            category,
            email: email,
            phone: phone.trim() === '' ? null : phone,
            address: address,
            address_number: addressNumber,
            address_complement: addressComplement,
            zip_code: zipCode,
            city: city,
            state: state,
            neighborhood: neighborhood,
            origin_sales_channel_uuid: originSalesChannelUuid,
            origin_sales_channel_name: originSalesChannelName,
        };

        api.post('customers', newCustomer)
            .then((response) => {
                hasResponse = true;
                console.log('Cliente registrado com sucesso:', response);
                navigate('/admin/customers');  // Redireciona de volta para a lista de impressoras
            })
            .catch((error) => {
                hasResponse = true;
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false); // Desativa o estado de loading
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    if (didNotLoad) {
        return <DidNotLoad/>
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/customers')} to={'/admin/customers'}>
                    Clientes
                </Link>
                <Typography color="text.primary">Cadastro de cliente</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Cadastro de cliente</Typography>
            <QuotaBlockedNotice quotaKey="customers" backTo="/admin/customers" />
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Identificação</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                        {resultErrors.name && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.name}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                            <InputLabel id="customer-category-label">Categoria</InputLabel>
                            <Select
                                labelId="customer-category-label"
                                label="Categoria"
                                value={category}
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                {CUSTOMER_CATEGORY_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {resultErrors.category && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.category}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <DocumentInput
                            documentNumber={documentNumber}
                            setDocumentNumber={setDocumentNumber}
                        />
                        {resultErrors.document && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.document}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SalesChannelSelect
                            label="Origem do cadastro"
                            value={originSalesChannelUuid}
                            onChange={(e) => setOriginSalesChannelUuid(e.target.value)}
                            onChannelChange={handleOriginChannelChange}
                        />
                        {resultErrors.origin_sales_channel_uuid && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.origin_sales_channel_uuid}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}><hr/></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Contatos</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <PhoneInput
                            label="Telefone/Whats"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={false}
                        />
                        {resultErrors.phone && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.phone}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="E-mail"
                            fullWidth
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {resultErrors.email && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.email}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12}><hr/></Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="h5">Endereço</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <ZipCodeInput
                            zipCode={zipCode}
                            setZipCode={setZipCode}
                            setCity={setCity}
                            setAddress={setAddress}
                            setState={setState}
                            setNeighborhood={setNeighborhood}
                        />
                        {resultErrors.zip_code && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.zip_code}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Endereço"
                            fullWidth
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                        />
                        {resultErrors.address && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.address}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Número"
                            fullWidth
                            value={addressNumber}
                            onChange={(e) => setAddressNumber(e.target.value)}
                            inputMode="numeric"
                        />
                        {resultErrors.address_number && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.address_number}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Complemento"
                            fullWidth
                            value={addressComplement}
                            onChange={(e) => setAddressComplement(e.target.value)}
                            inputMode="numeric"
                        />
                        {resultErrors.address_complement && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.address_complement}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Bairro"
                            fullWidth
                            value={neighborhood}
                            onChange={(e) => setNeighborhood(e.target.value)}
                        />
                        {resultErrors.neighborhood && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.neighborhood}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Cidade"
                            fullWidth
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />
                        {resultErrors.city && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.city}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <StateSelectInput
                            state={state}
                            setState={setState}
                        />
                        {resultErrors.state && (
                            <div
                                className="text-danger mt-1"
                                style={{whiteSpace: 'pre-wrap', fontSize: '0.9rem'}}
                            >
                                {resultErrors.state}
                            </div>
                        )}
                    </Grid>

                </Grid>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' }, // Empilha no mobile e mantém lado a lado no desktop
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginTop: 2,
                        marginBottom: 8,
                        gap: 2 // Adiciona espaçamento entre os itens no mobile
                    }}
                >
                    {/* Mensagem de erro à esquerda */}
                    {resultErrors.has_errors ? (
                        <div
                            className="text-danger"
                            style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                        >
                            Ocorreram erros de validação, verifique os campos acima.
                        </div>
                    ) : (
                        <div style={{ height: '1rem' }} />
                    )}

                    {/* Botão responsivo */}
                    <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={isSubmitting}
                            startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                            sx={{ width: '100%' }} // Garante que o botão fique full-width no mobile
                        >
                            {isSubmitting ? 'Cadastrando...' : 'Cadastrar cliente'}
                        </Button>
                    </Box>
                </Box>
            </form>
        </div>

    );
}

export default RegisterCustomerPage;
